import type { RefObject } from 'react';
import { breakpoints, Button, Inline, Stack, Text, textVariants } from '@etg/wings';
import { ContentWrapper, variants } from '@eti/components';
import { useProperty, useTranslation } from '@eti/providers';
import { css, etiColors, mediaQueries } from '@eti/styles';
import { useMediaQuery } from 'usehooks-ts';
import EtgFaresPrice from './EtgFaresPrice';

const stickyFooterStyles = css`
  align-items: center;
  bottom: 0;
  display: flex;
  margin-top: auto;
  position: sticky;
  width: 100%;
`;

const buttonContainerStyles = (isDesktop: boolean) => css`
  background-color: ${etiColors.white};
  border-top: solid #e5e5e5 1px;
  height: ${isDesktop ? '84px' : '80px'};
  width: 100%;
`;

const contentWrapperStyles = css`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
`;

const inlineStyles = css`
  flex: 1;

  @media ${mediaQueries.medium.up} {
    flex: initial;
  }
`;

interface EtgFaresStickyFooterProps {
  handlePriceModalOpen: () => void;
  includedInTripProductPrice: number;
  onProceedClick: () => void;
  onSkipStepClick: () => {};
  numberOfTravelersWithoutInfants: number;
  selectedBundlePrice: number;
  skipButtonRef: RefObject<HTMLButtonElement>;
  totalTripPrice: number;
  tripPricePerAdult: number;
}

const EtgFaresStickyFooter = ({
  handlePriceModalOpen,
  includedInTripProductPrice,
  onProceedClick,
  onSkipStepClick,
  numberOfTravelersWithoutInfants,
  selectedBundlePrice,
  skipButtonRef,
  totalTripPrice,
  tripPricePerAdult,
}: EtgFaresStickyFooterProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const footerVersionTwoEnabled = p('ETG.Fares.Footer.Layout.V2');
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints._768})`);

  const priceComponent = (
    <EtgFaresPrice
      handlePriceModalOpen={handlePriceModalOpen}
      includedInTripProductPrice={includedInTripProductPrice}
      numberOfTravelersWithoutInfants={numberOfTravelersWithoutInfants}
      selectedBundlePrice={selectedBundlePrice}
      totalTripPrice={totalTripPrice}
      tripPricePerAdult={tripPricePerAdult}
    />
  );

  const abTestFooterControl = (
    <>
      <Inline alignY="center" className={inlineStyles}>
        <Button
          className={inlineStyles}
          data-testid="etgFares-skipStepButton"
          onClick={onSkipStepClick}
          ref={skipButtonRef}
          variant={variants.SECONDARY}
        >
          {t('YourSelectedTrip.Page.SkipStepButton.Label')}
        </Button>
      </Inline>
      <Inline alignY="center" className={inlineStyles} spacing={isDesktop ? 16 : 8}>
        {selectedBundlePrice > 0 && priceComponent}
        <Button
          className={inlineStyles}
          data-testid="etgFares-proceedButton"
          onClick={onProceedClick}
          variant={variants.PRIMARY}
        >
          {t('YourSelectedTrip.Page.ProceedButton.EtgFares.Label')}
        </Button>
      </Inline>
    </>
  );

  const abTestFooterVariation = (
    <>
      <Text id="totalPriceText" variant={textVariants._16_BOLD}>
        {selectedBundlePrice > 0 && t('Product.ETG.Fares.Total.Price')}
      </Text>
      <Inline alignY="center" className={inlineStyles} spacing={isDesktop ? 16 : 8}>
        {selectedBundlePrice > 0 && priceComponent}
        <Button
          className={inlineStyles}
          data-testid="etgFares-proceedButton"
          onClick={onProceedClick}
          variant={variants.PRIMARY}
        >
          {t('YourSelectedTrip.Page.ProceedButton.EtgFares.Label')}
        </Button>
        <Button
          className={inlineStyles}
          data-testid="etgFares-skipStepButton"
          onClick={onSkipStepClick}
          ref={skipButtonRef}
          variant={variants.SECONDARY}
        >
          {t('YourSelectedTrip.Page.SkipStepButton.Label')}
        </Button>
      </Inline>
    </>
  );

  return (
    <Stack className={stickyFooterStyles} data-testid="etg-fares-sticky-footer" spacing={0}>
      {!isDesktop && selectedBundlePrice > 0 && priceComponent}
      <Inline className={buttonContainerStyles(isDesktop)} noWrap spacing={16}>
        <ContentWrapper className={contentWrapperStyles}>
          {isDesktop && (footerVersionTwoEnabled ? abTestFooterVariation : abTestFooterControl)}
          {!isDesktop && (
            <Inline className={inlineStyles} noWrap>
              <Button
                className={inlineStyles}
                data-testid="etgFares-skipStepButton"
                onClick={onSkipStepClick}
                ref={skipButtonRef}
                variant={variants.SECONDARY}
              >
                {t('YourSelectedTrip.Page.SkipStepButton.Label')}
              </Button>
              <Button
                className={inlineStyles}
                data-testid="etgFares-proceedButton"
                onClick={onProceedClick}
                variant={variants.PRIMARY}
              >
                {t('YourSelectedTrip.Page.ProceedButton.EtgFares.Label')}
              </Button>
            </Inline>
          )}
        </ContentWrapper>
      </Inline>
    </Stack>
  );
};

export default EtgFaresStickyFooter;
